import styled from 'styled-components';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { useRemoveOrganizationMember } from 'api/OrganizationAPI';
import { Modal, ModalBody, ModalHeader, ModalFooter, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { colors, Body2, BoldText } from 'ui/theme';
import { BUTTON_SIZES, DestructiveButton } from 'ui/button';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useSelectionContext } from '../../contexts/SelectionContext';
import {
  MODAL_NAMES,
  MemberPayload,
  useOrganizationModalsContext,
} from '../../contexts/OrganizationModalsContext';
import { useOrganizationMembersQueryContext } from '../../contexts/OrganizationMembersQueryContext';
import { fullName } from '../../utils';

const Body2Light = styled(Body2)`
  color: ${colors.textSecondary};
`;

export const RevokeAccessModal = ({
  rerouteOnSuccess,
  deselectOnSuccess,
}: {
  rerouteOnSuccess?: boolean;
  deselectOnSuccess?: boolean;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { deselectByIds } = useSelectionContext();
  const { refetchAll } = useOrganizationMembersQueryContext();
  const { hideModal, modalState } = useOrganizationModalsContext();

  const member = (modalState?.payload as MemberPayload)?.member;

  const { mutate, isLoading } = useRemoveOrganizationMember(member.userId, {
    onSuccess: () => {
      refetchAll();
      dispatch(addPositiveToast(`Access for ${fullName(member)} was revoked successfully.`));
      /** If used on Member Details, should reroute to Members Index on success */
      if (rerouteOnSuccess) history.push('/members');
      /** If used on Members Index, should deselect user id, in case modal was triggered via Bulk Actions Bar */
      if (deselectOnSuccess) deselectByIds([member.userId]);
      hideModal();
    },
    onError: (error: unknown) => {
      const msg = `There was an error revoking access for ${fullName(member)}. Please try again`;
      dispatch(addNegativeToastWithErrorId(msg, 'revokeAccess', error));
      hideModal();
    },
  });

  const onSubmit = () => {
    mutate(undefined);
  };

  return (
    <Modal
      id="revoke-access-modal"
      isModalOpen={modalState?.name === MODAL_NAMES.REVOKE_ACCESS}
      onClose={hideModal}
      variant={MODAL_VARIANTS.DELETE}
      size={MODAL_SIZES.SMALL}
    >
      <ModalHeader>Revoke access?</ModalHeader>
      <ModalBody>
        <Body2Light>
          Are you sure you want to revoke access for{' '}
          <BoldText>
            {fullName(member)} ({member.email})
          </BoldText>
          ?
        </Body2Light>
        <Body2Light>
          They’ll no longer be able to login and access any projects in your organization.
        </Body2Light>
      </ModalBody>
      <ModalFooter>
        <DestructiveButton
          size={BUTTON_SIZES.LARGE}
          onClick={onSubmit}
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          Revoke access
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
